import styles from "./AdditionalAddonsModal.module.scss";
import { TiMinus, TiPlus } from "react-icons/ti";
import { Switch } from "react-animated-switch";
import Modal from "../../Common/Modal/Modal";
import React from "react";

type AddonOption = {
  addonId: number;
  amenityName: string;
  noofAddonunits: number;
  availableNoofAddonunits: number;
  cost: number;
  terms: string;
  image: string;
  amenityId: number;
};

type AddonSelection = {
  addonId: number;
  noofAddonUnits: number;
  notifyAddon: boolean;
};

interface Props {
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  addonOptions?: AddonOption[];
  addonSelections: AddonSelection[];
  setAddonSelections: React.Dispatch<React.SetStateAction<AddonSelection[]>>;
}

const AdditionalAddonsModal = (props: Props) => {
  const {
    isOpen,
    setIsOpen,
    addonOptions,
    addonSelections,
    setAddonSelections,
  } = props;

  const getSel = (addonId: number) =>
    addonSelections.find((s) => s.addonId === addonId) ?? {
      addonId: addonId,
      noofAddonUnits: 0,
      notifyAddon: false,
    };

  const updateSel = (addonId: number, patch: Partial<AddonSelection>) => {
    setAddonSelections((prev) => {
      const id = addonId;

      const i = prev.findIndex((s) => s.addonId === id);

      const cur =
        i >= 0
          ? prev[i]
          : {
              addonId: id,
              noofAddonUnits: 0,
              notifyAddon: false,
            };

      const next = { ...cur, ...patch };

      return i >= 0
        ? prev.map((s, j) => (j === i ? next : s))
        : [...prev, next];
    });
  };

  const changeCount = (opt: AddonOption, delta: number) => {
    const s = getSel(opt.addonId);
    const max = opt.noofAddonunits ?? 0;
    const next = s.noofAddonUnits + delta;

    if (next >= 0 && next <= max) {
      updateSel(opt.addonId, { noofAddonUnits: next });
    }
  };

  return (
    <Modal isOpen={isOpen} setIsOpen={setIsOpen} showCloseIcon={true}>
      <div className={styles.additionalAmenitiesModalContainer}>
        <div className={styles.headingContainer}>
          <p className={styles.heading}>Select Additional Addons</p>
        </div>
        <div className={styles.parkingGroupContainer}>
          {addonOptions?.map((opt) => {
            const s = getSel(opt.addonId);

            const overLimit =
              s.noofAddonUnits > (opt.availableNoofAddonunits ?? 0);

            const noneAvailable = (opt.availableNoofAddonunits ?? 0) === 0;

            const showNotifySwitch = overLimit;

            return (
              <div key={opt.addonId} className={styles.parkingGroup}>
                {(overLimit || noneAvailable) && (
                  <div className={styles.notAvailableContainer}>
                    <p className={styles.label}>Unavailable</p>
                  </div>
                )}
                {opt.amenityName && (
                  <p className={styles.parkingGroupHeading}>
                    {opt.amenityName}
                  </p>
                )}

                <div className={styles.parkingContainer}>
                  <div className={styles.contentContainer}>
                    <div className={styles.contentInnerContainer}>
                      <div className={styles.labelContainer}>
                        <p className={styles.label}>
                          Addons Available ({opt?.availableNoofAddonunits ?? 0})
                        </p>
                      </div>

                      <div className={styles.counterContainer}>
                        <button
                          className={styles.counterButton}
                          disabled={noneAvailable}
                          onClick={() => changeCount(opt, -1)}
                        >
                          <TiMinus />
                        </button>

                        <p className={styles.counterValue}>
                          {s.noofAddonUnits}
                        </p>

                        <button
                          className={styles.counterButton}
                          disabled={noneAvailable}
                          onClick={() => changeCount(opt, 1)}
                        >
                          <TiPlus />
                        </button>
                      </div>

                      <div className={styles.priceContainer}>
                        <p>$ {(opt.cost ?? 0) * s.noofAddonUnits}</p>
                      </div>
                    </div>
                  </div>
                </div>

                {showNotifySwitch && (
                  <div className={styles.noticeContainer}>
                    <div className={styles.switchContainer}>
                      <Switch
                        inActiveTrackColor="#DFDFDF"
                        activeTrackColor="#C0F496"
                        inActiveThumbColor="#000000"
                        activeThumbColor="#000000"
                        checked={s.notifyAddon}
                        onChange={(val) =>
                          updateSel(opt.addonId, {
                            notifyAddon: val,
                          })
                        }
                      />
                    </div>

                    <p className={styles.label}>
                      Notify me when addon is available
                    </p>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div className={styles.buttonContainer}>
          <button className={styles.button} onClick={() => setIsOpen(false)}>
            Continue
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default AdditionalAddonsModal;
